/**
 * DatePicker — and `CalendarMonth`, the month grid the range picker reuses.
 *
 * The grid is a real `role="grid"` with a roving tabindex: exactly one day is
 * tabbable, arrows move by day, PageUp/PageDown by month, Shift+PageUp/Down by
 * year, Home/End to the ends of the week. Dates are handled as *local calendar
 * days* and serialised as `yyyy-MM-dd`, so a picked day never slides across a
 * timezone boundary on the way to the API.
 */

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  nextMonday,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { ISODate } from '@shared/types';
import { cn } from '@/lib/cn';
import { composeRefs } from './Button';
import type { ControlSize } from './Button';
import { FIELD_PAD, fieldShell } from './Input';

/* ── Date plumbing ───────────────────────────────────────────────────────── */

export type WeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** `yyyy-MM-dd` — an ISO-8601 calendar date, free of timezone drift. */
export function toISODate(date: Date): ISODate {
  return format(date, 'yyyy-MM-dd');
}

/** Reads either `yyyy-MM-dd` or a full ISO instant as a local calendar day. */
export function parseISODate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (parts) return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
}

function dayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function clampToBounds(date: Date, min?: Date | null, max?: Date | null): Date {
  if (min && isBefore(date, min)) return min;
  if (max && isAfter(date, max)) return max;
  return date;
}

function isOutOfBounds(date: Date, min?: Date | null, max?: Date | null): boolean {
  if (min && isBefore(date, startOfDay(min))) return true;
  if (max && isAfter(date, startOfDay(max))) return true;
  return false;
}

/* ── CalendarMonth ───────────────────────────────────────────────────────── */

export interface CalendarRange {
  from: Date | null;
  to: Date | null;
}

export interface CalendarMonthProps {
  /** Any date inside the month to render. */
  month: Date;
  selected?: Date | null;
  range?: CalendarRange | null;
  /** Live end of an in-progress range, for the hover preview. */
  preview?: Date | null;
  focusedDate: Date;
  onFocusedDateChange: (date: Date) => void;
  onSelect: (date: Date) => void;
  onHoverDate?: (date: Date | null) => void;
  min?: Date | null;
  max?: Date | null;
  weekStartsOn?: WeekStart;
  today?: Date;
  /** Only the grid holding `focusedDate` takes the tab stop. */
  isFocusOwner?: boolean;
  /** Bump to pull DOM focus onto the focused cell. */
  focusToken?: number;
  className?: string;
}

export function CalendarMonth({
  month,
  selected = null,
  range = null,
  preview = null,
  focusedDate,
  onFocusedDateChange,
  onSelect,
  onHoverDate,
  min = null,
  max = null,
  weekStartsOn = 1,
  today = new Date(),
  isFocusOwner = true,
  focusToken = 0,
  className,
}: CalendarMonthProps) {
  const cellRefs = useRef(new Map<string, HTMLButtonElement | null>());
  const monthStart = startOfMonth(month);

  const weeks = useMemo(() => {
    const gridStart = startOfWeek(monthStart, { weekStartsOn });
    const gridEnd = endOfWeek(endOfMonth(monthStart), { weekStartsOn });
    const out: Date[][] = [];
    let cursor = gridStart;
    while (!isAfter(cursor, gridEnd)) {
      const week: Date[] = [];
      for (let i = 0; i < 7; i += 1) {
        week.push(cursor);
        cursor = addDays(cursor, 1);
      }
      out.push(week);
    }
    return out;
  }, [monthStart, weekStartsOn]);

  const weekdayLabels = useMemo(
    () =>
      weeks[0]?.map((day) => ({
        key: dayKey(day),
        short: format(day, 'EEEEE'),
        full: format(day, 'EEEE'),
      })) ?? [],
    [weeks],
  );

  useEffect(() => {
    if (!isFocusOwner || focusToken === 0) return;
    cellRefs.current.get(dayKey(focusedDate))?.focus();
  }, [focusToken, focusedDate, isFocusOwner]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let next: Date | null = null;
    switch (event.key) {
      case 'ArrowLeft':
        next = addDays(focusedDate, -1);
        break;
      case 'ArrowRight':
        next = addDays(focusedDate, 1);
        break;
      case 'ArrowUp':
        next = addWeeks(focusedDate, -1);
        break;
      case 'ArrowDown':
        next = addWeeks(focusedDate, 1);
        break;
      case 'Home':
        next = startOfWeek(focusedDate, { weekStartsOn });
        break;
      case 'End':
        next = endOfWeek(focusedDate, { weekStartsOn });
        break;
      case 'PageUp':
        next = event.shiftKey ? addYears(focusedDate, -1) : addMonths(focusedDate, -1);
        break;
      case 'PageDown':
        next = event.shiftKey ? addYears(focusedDate, 1) : addMonths(focusedDate, 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOutOfBounds(focusedDate, min, max)) onSelect(focusedDate);
        return;
      default:
        return;
    }
    event.preventDefault();
    onFocusedDateChange(clampToBounds(next, min, max));
  };

  const rangeFrom = range?.from ?? null;
  const rangeTo = range?.to ?? preview ?? null;
  const spanStart = rangeFrom && rangeTo ? (isAfter(rangeFrom, rangeTo) ? rangeTo : rangeFrom) : null;
  const spanEnd = rangeFrom && rangeTo ? (isAfter(rangeFrom, rangeTo) ? rangeFrom : rangeTo) : null;

  return (
    // 7 × 32px cells, so every day box is square.
    <div className={cn('w-56 select-none', className)}>
      <div className="flex h-7 items-center justify-center text-sm font-semibold text-primary">
        {format(monthStart, 'MMMM yyyy')}
      </div>

      <div
        role="grid"
        aria-label={format(monthStart, 'MMMM yyyy')}
        onKeyDown={handleKeyDown}
        onPointerLeave={() => onHoverDate?.(null)}
      >
        <div role="row" className="grid grid-cols-7">
          {weekdayLabels.map((day) => (
            <abbr
              key={day.key}
              role="columnheader"
              aria-label={day.full}
              title={day.full}
              className="eyebrow grid h-6 place-items-center text-muted! no-underline"
            >
              {day.short}
            </abbr>
          ))}
        </div>

        {weeks.map((week) => (
          <div role="row" key={dayKey(week[0] as Date)} className="grid grid-cols-7">
            {week.map((day) => {
              const key = dayKey(day);
              const outside = !isSameMonth(day, monthStart);
              const disabled = isOutOfBounds(day, min, max);
              const isSelected =
                (selected !== null && isSameDay(day, selected)) ||
                (rangeFrom !== null && isSameDay(day, rangeFrom)) ||
                (range?.to != null && isSameDay(day, range.to));
              const inSpan =
                spanStart !== null &&
                spanEnd !== null &&
                !isBefore(day, spanStart) &&
                !isAfter(day, spanEnd);
              const isSpanStart = spanStart !== null && isSameDay(day, spanStart);
              const isSpanEnd = spanEnd !== null && isSameDay(day, spanEnd);
              const isToday = isSameDay(day, today);
              const isFocusTarget = isSameDay(day, focusedDate);

              return (
                <div
                  role="gridcell"
                  aria-selected={isSelected}
                  key={key}
                  className={cn(
                    'relative h-8',
                    inSpan && !isSelected && 'bg-selected',
                    inSpan && isSpanStart && 'rounded-l-sm',
                    inSpan && isSpanEnd && 'rounded-r-sm',
                  )}
                >
                  <button
                    ref={(node) => {
                      cellRefs.current.set(key, node);
                    }}
                    type="button"
                    tabIndex={isFocusOwner && isFocusTarget ? 0 : -1}
                    disabled={disabled}
                    aria-label={format(day, 'EEEE, d MMMM yyyy')}
                    aria-current={isToday ? 'date' : undefined}
                    onClick={() => onSelect(day)}
                    onPointerEnter={() => onHoverDate?.(day)}
                    onFocus={() => {
                      if (!isFocusTarget) onFocusedDateChange(day);
                    }}
                    className={cn(
                      'absolute inset-0 grid place-items-center rounded-sm text-sm tnum',
                      'transition-[background-color,color] duration-[var(--dur-instant)] ease-[var(--ease-out)]',
                      outside ? 'text-muted' : 'text-secondary',
                      !disabled && !isSelected && 'hover:bg-hover hover:text-primary',
                      inSpan && !isSelected && 'text-primary',
                      /* Gold means create in this product and nothing else, so a
                         selected day is `--focus` — the token that already means
                         "the thing the reader is on", carrying `--text-inverse`
                         as its ink at the same 6.46:1 dark / 8.00:1 light
                         `Checkbox` and `Switch` measured for the identical job.
                         Today is a location marker and is marked by WEIGHT, not
                         by a hue: `--accent-text` on it made gold mean create on
                         the Save view button 40px away and "today" inside this
                         popover. Only the 4px dot keeps a colour, and it is the
                         selection's. */
                      isToday && !isSelected && 'font-semibold text-primary',
                      isSelected && 'bg-focus font-semibold text-inverse',
                      disabled && 'cursor-not-allowed text-faint hover:bg-transparent',
                    )}
                  >
                    {format(day, 'd')}
                    {isToday ? (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute bottom-1 size-1 rounded-full',
                          isSelected ? 'bg-inverse' : 'bg-focus',
                        )}
                      />
                    ) : null}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Presets ─────────────────────────────────────────────────────────────── */

export interface DatePreset {
  key: string;
  label: string;
  get: (today: Date) => Date;
}

export const DEFAULT_DATE_PRESETS: DatePreset[] = [
  { key: 'today', label: 'Today', get: (t) => t },
  { key: 'tomorrow', label: 'Tomorrow', get: (t) => addDays(t, 1) },
  { key: 'in-3-days', label: 'In 3 days', get: (t) => addDays(t, 3) },
  { key: 'next-monday', label: 'Next Monday', get: (t) => nextMonday(t) },
  { key: 'in-2-weeks', label: 'In 2 weeks', get: (t) => addWeeks(t, 2) },
];

export const PRESET_RAIL = 'flex w-32 shrink-0 flex-col gap-px border-r border-line-subtle p-1';

export const PRESET_ITEM = [
  'flex h-7 items-center rounded-xs! px-2 text-left text-sm text-secondary',
  'transition-colors duration-[var(--dur-instant)] ease-[var(--ease-out)]',
  'hover:bg-hover hover:text-primary',
].join(' ');

/* ── DatePicker ──────────────────────────────────────────────────────────── */

export interface DatePickerProps {
  value?: ISODate | null;
  onChange?: (value: ISODate | null) => void;
  /** Pass `[]` to hide the rail. */
  presets?: DatePreset[];
  clearable?: boolean;
  placeholder?: string;
  size?: ControlSize;
  invalid?: boolean;
  disabled?: boolean;
  min?: ISODate | null;
  max?: ISODate | null;
  weekStartsOn?: WeekStart;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Overrides "now" so screenshots stay reproducible. */
  today?: Date;
  footer?: ReactNode;
}

export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(function DatePicker(
  {
    value,
    onChange,
    presets = DEFAULT_DATE_PRESETS,
    clearable = true,
    placeholder = 'Pick a date',
    size = 'md',
    invalid = false,
    disabled = false,
    min,
    max,
    weekStartsOn = 1,
    id,
    className,
    align = 'start',
    side = 'bottom',
    today: todayProp,
    footer,
    ...aria
  },
  ref,
) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const today = useMemo(() => startOfDay(todayProp ?? new Date()), [todayProp]);
  const selected = useMemo(() => parseISODate(value), [value]);
  const minDate = useMemo(() => parseISODate(min), [min]);
  const maxDate = useMemo(() => parseISODate(max), [max]);

  const [open, setOpen] = useState(false);
  const [focusedDate, setFocusedDate] = useState<Date>(() => selected ?? today);
  const [focusToken, setFocusToken] = useState(0);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        setFocusedDate(clampToBounds(selected ?? today, minDate, maxDate));
        setFocusToken((token) => token + 1);
      }
    },
    [maxDate, minDate, selected, today],
  );

  const commit = useCallback(
    (date: Date) => {
      onChange?.(toISODate(date));
      setOpen(false);
      triggerRef.current?.focus();
    },
    [onChange],
  );

  return (
    <RPopover.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <RPopover.Trigger asChild>
        <button
          ref={composeRefs(ref, triggerRef)}
          type="button"
          id={id}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          onKeyDown={(event) => {
            // The clear affordance inside the trigger cannot be focusable (a
            // button inside a button is invalid markup), so the key clears.
            if (clearable && selected && (event.key === 'Backspace' || event.key === 'Delete')) {
              event.preventDefault();
              onChange?.(null);
            }
          }}
          className={cn(
            'group',
            fieldShell({ size, invalid, disabled, focus: 'visible' }),
            FIELD_PAD[size],
            'justify-start text-left',
            'data-[state=open]:border-focus data-[state=open]:shadow-[inset_0_0_0_1px_var(--focus)]',
            className,
          )}
          {...aria}
        >
          <CalendarDays aria-hidden="true" className="size-3.5 shrink-0 text-muted" />
          <span
            className={cn(
              'min-w-0 flex-1 truncate font-mono text-sm tnum',
              selected ? 'text-primary' : 'text-muted',
            )}
          >
            {selected ? format(selected, 'MMM d, yyyy') : placeholder}
          </span>
          {clearable && selected ? (
            <span
              aria-hidden="true"
              title="Clear date (Backspace)"
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onChange?.(null);
              }}
              className="grid size-4 shrink-0 place-items-center rounded-xs text-muted hover:bg-hover hover:text-primary"
            >
              <X className="size-3" />
            </span>
          ) : null}
        </button>
      </RPopover.Trigger>

      <RPopover.Portal>
        <RPopover.Content
          align={align}
          side={side}
          sideOffset={4}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className={cn(
            'z-[var(--z-popover)] flex overflow-hidden rounded-md border border-line',
            'bg-surface-overlay shadow-3',
          )}
          style={{ animation: 'af-scale-in var(--dur-fast) var(--ease-out)' }}
        >
          {presets.length > 0 ? (
            <div className={PRESET_RAIL}>
              {presets.map((preset) => {
                const presetDate = startOfDay(preset.get(today));
                const active = selected !== null && isSameDay(presetDate, selected);
                return (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => commit(presetDate)}
                    className={cn(PRESET_ITEM, active && 'bg-selected text-primary')}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="relative p-2">
            <div className="absolute inset-x-2 top-2 flex h-7 items-center justify-between">
              <button
                type="button"
                aria-label="Previous month"
                onClick={() => setFocusedDate((date) => addMonths(date, -1))}
                className="grid size-6 place-items-center rounded-xs! text-muted hover:bg-hover hover:text-primary"
              >
                <ChevronLeft aria-hidden="true" className="size-4" />
              </button>
              <button
                type="button"
                aria-label="Next month"
                onClick={() => setFocusedDate((date) => addMonths(date, 1))}
                className="grid size-6 place-items-center rounded-xs! text-muted hover:bg-hover hover:text-primary"
              >
                <ChevronRight aria-hidden="true" className="size-4" />
              </button>
            </div>

            <CalendarMonth
              month={focusedDate}
              selected={selected}
              focusedDate={focusedDate}
              onFocusedDateChange={setFocusedDate}
              onSelect={commit}
              min={minDate}
              max={maxDate}
              weekStartsOn={weekStartsOn}
              today={today}
              focusToken={focusToken}
            />

            {footer ?? (
              <div className="mt-2 flex items-center justify-between border-t border-line-subtle pt-2">
                <button
                  type="button"
                  onClick={() => commit(today)}
                  className="rounded-xs! px-1.5 py-1 text-sm text-secondary hover:bg-hover hover:text-primary"
                >
                  Today
                </button>
                {clearable ? (
                  <button
                    type="button"
                    onClick={() => {
                      onChange?.(null);
                      setOpen(false);
                    }}
                    className="rounded-xs! px-1.5 py-1 text-sm text-muted hover:bg-hover hover:text-primary"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
});
